import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import {
  type VendorCreateFormValues,
  type VendorCreateInput,
  type VendorListItem,
  VendorCreateInput as VendorCreateInputSchema,
  VendorListItem as VendorListItemSchema,
} from '@uit/shared/schemas/vendor';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { apiMutation } from '../../lib/api.js';
import s from './CreateVendorDialog.module.scss';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (vendor: VendorListItem) => void;
}

/**
 * Manual vendor create (T-199). Only the business name is required — the case
 * this exists for is a vendor that already lives in Eric's QuickBooks and just
 * needs to exist here so an engagement can point at it. The rest of the
 * business identity is optional and collapsed; it can be filled in later, and
 * the vendors list flags the record Incomplete until it is.
 */
export function CreateVendorDialog({ open, onOpenChange, onCreated }: Props) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showOptional, setShowOptional] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VendorCreateFormValues, unknown, VendorCreateInput>({
    resolver: zodResolver(VendorCreateInputSchema),
    defaultValues: { business_name: '' },
  });

  function close(next: boolean): void {
    onOpenChange(next);
    if (!next) {
      reset();
      setSubmitError(null);
      setShowOptional(false);
    }
  }

  const onSubmit = async (data: VendorCreateInput): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Blank optional inputs are already null here — VendorCreateInput
      // coerces them, so both the resolver and the API agree on the shape.
      const created = await apiMutation<VendorCreateInput, VendorListItem>('/api/vendors', data, {
        schema: VendorListItemSchema,
      });
      onCreated(created);
      close(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('409')) {
        setSubmitError('A vendor with that name already exists.');
      } else if (message.includes('403')) {
        setSubmitError('You do not have permission to create vendors.');
      } else if (message.includes('400')) {
        setSubmitError('The form has validation errors. Please check the fields above.');
      } else {
        setSubmitError('Could not create vendor. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.content}>
          <Dialog.Title className={s.title}>New vendor</Dialog.Title>
          <Dialog.Description className={s.description}>
            Creates the business record only — no resource, engagement, or QuickBooks entry. Use
            this to introduce a vendor that already exists elsewhere so an engagement can be pointed
            at it.
          </Dialog.Description>

          <form
            onSubmit={(e) => {
              void handleSubmit(onSubmit)(e);
            }}
            noValidate
          >
            {submitError && (
              <div className={s.formError} role="alert">
                {submitError}
              </div>
            )}

            <div className={s.field}>
              <label className={s.label} htmlFor="vendor-business-name">
                Business name
              </label>
              <input
                id="vendor-business-name"
                type="text"
                className={s.input}
                aria-invalid={!!errors.business_name}
                {...register('business_name')}
              />
              {errors.business_name && (
                <div className={s.fieldError}>{errors.business_name.message}</div>
              )}
            </div>

            {!showOptional && (
              <button
                type="button"
                className={s.optionalToggle}
                onClick={() => setShowOptional(true)}
              >
                + Add contact and address details
              </button>
            )}

            {showOptional && (
              <>
                <div className={s.field}>
                  <label className={s.label} htmlFor="vendor-company-type">
                    Company type
                  </label>
                  <select
                    id="vendor-company-type"
                    className={s.select}
                    {...register('company_type')}
                  >
                    <option value="">—</option>
                    <option value="LLC">LLC</option>
                    <option value="C_CORP">C-Corp</option>
                    <option value="S_CORP">S-Corp</option>
                  </select>
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="vendor-business-email">
                    Business email
                  </label>
                  <input
                    id="vendor-business-email"
                    type="email"
                    className={s.input}
                    aria-invalid={!!errors.business_email}
                    {...register('business_email')}
                  />
                  {errors.business_email && (
                    <div className={s.fieldError}>{errors.business_email.message}</div>
                  )}
                </div>

                <div className={s.field}>
                  <div className={s.row}>
                    <div>
                      <label className={s.label} htmlFor="vendor-mobile-phone">
                        Mobile phone
                      </label>
                      <input
                        id="vendor-mobile-phone"
                        type="tel"
                        className={s.input}
                        {...register('mobile_phone')}
                      />
                    </div>
                    <div>
                      <label className={s.label} htmlFor="vendor-office-phone">
                        Office phone
                      </label>
                      <input
                        id="vendor-office-phone"
                        type="tel"
                        className={s.input}
                        {...register('office_phone')}
                      />
                    </div>
                  </div>
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="vendor-street">
                    Street
                  </label>
                  <input
                    id="vendor-street"
                    type="text"
                    className={s.input}
                    {...register('business_address_street')}
                  />
                </div>

                <div className={s.field}>
                  <div className={s.row}>
                    <div>
                      <label className={s.label} htmlFor="vendor-city">
                        City
                      </label>
                      <input
                        id="vendor-city"
                        type="text"
                        className={s.input}
                        {...register('business_address_city')}
                      />
                    </div>
                    <div className={s.rowNarrow}>
                      <label className={s.label} htmlFor="vendor-state">
                        State
                      </label>
                      <input
                        id="vendor-state"
                        type="text"
                        className={s.input}
                        aria-invalid={!!errors.business_address_state}
                        {...register('business_address_state')}
                      />
                    </div>
                    <div className={s.rowNarrow}>
                      <label className={s.label} htmlFor="vendor-zip">
                        ZIP
                      </label>
                      <input
                        id="vendor-zip"
                        type="text"
                        className={s.input}
                        {...register('business_address_zip')}
                      />
                    </div>
                  </div>
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="vendor-state-of-incorporation">
                    State of incorporation
                  </label>
                  <input
                    id="vendor-state-of-incorporation"
                    type="text"
                    className={s.input}
                    aria-invalid={!!errors.state_of_incorporation}
                    {...register('state_of_incorporation')}
                  />
                </div>
              </>
            )}

            <div className={s.actions}>
              <button
                type="button"
                className={s.cancel}
                onClick={() => {
                  close(false);
                }}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className={s.submit} disabled={submitting}>
                {submitting ? 'Creating…' : 'Create vendor'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
